import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";

import {
  Lede,
  MarketingFooter,
  MarketingHeader,
  Section,
  SectionHeading,
} from "@/components/marketing/parts";
import { PlanCheckout } from "@/components/PlanCheckout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { submitBusinessEnquiry } from "@/lib/business.functions";
import {
  BUSINESS_PRICE_ID,
  MIN_SEATS,
  SEAT_PENCE,
  TEAM_PRICE_ID,
  formatGbp,
} from "@/lib/entitlements";

export const Route = createFileRoute("/business")({
  head: () => ({
    meta: [
      { title: "MyPriorities for Business — CRM, contracts, revenue and reporting in one place" },
      {
        name: "description",
        content:
          "Per-user plans for teams who want priorities, tasks, CRM, pipeline, contracts, revenue, renewals and reporting in one simple workspace.",
      },
      {
        property: "og:title",
        content: "MyPriorities for Business — CRM, contracts, revenue and reporting in one place",
      },
      {
        property: "og:description",
        content:
          "Team £7.99 per user/month, Business £14.99 per user/month, plus Enterprise. Fewer tools, less admin, better visibility.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://mypriorities.co.uk/business" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://mypriorities.co.uk/business" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "MyPriorities for Business",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          url: "https://mypriorities.co.uk/business",
          description:
            "Priorities, tasks, CRM, pipeline, contracts, revenue, renewals and reporting in one simple workspace.",
          offers: [
            {
              "@type": "Offer",
              name: "Team",
              price: "7.99",
              priceCurrency: "GBP",
              url: "https://mypriorities.co.uk/business",
            },
            {
              "@type": "Offer",
              name: "Business",
              price: "14.99",
              priceCurrency: "GBP",
              url: "https://mypriorities.co.uk/business",
            },
          ],
        }),
      },
    ],
  }),
  component: BusinessPage,
});

type PaidPlan = "team" | "business";

const PLANS: Record<
  PaidPlan,
  { name: string; priceId: string; pitch: string; features: string[] }
> = {
  team: {
    name: "Team",
    priceId: TEAM_PRICE_ID,
    pitch: "For a small team that wants its work, customers and deals in one place.",
    features: [
      "Everything in Pro for every seat",
      "Shared tasks, allocation and honest team capacity",
      "CRM: accounts, contacts, opportunities and pipeline",
      "Contracts, renewals and revenue tracking",
      "Delivery timelines and RAG views",
    ],
  },
  business: {
    name: "Business",
    priceId: BUSINESS_PRICE_ID,
    pitch: "For businesses running several teams and reporting on the numbers.",
    features: [
      "Everything in Team, plus Premium AI for every seat",
      "Manager dashboards across linked accounts",
      "Business cases and your own price book",
      "Revenue management, sign-offs and evidence trails",
      "Slide-ready reporting and priority support",
    ],
  },

};

function BusinessPage() {
  const [seats, setSeats] = useState(5);
  const [checkoutPlan, setCheckoutPlan] = useState<PaidPlan | null>(null);

  return (
    <div className="min-h-screen bg-background">
      <MarketingHeader signedIn={false} />

      <section className="mx-auto max-w-5xl px-6 pb-8 pt-20">
        <p className="label-caps">MyPriorities for Business</p>
        <h1 className="mt-4 max-w-3xl text-4xl font-bold leading-tight sm:text-5xl">
          Run the business from one workspace, not six subscriptions.
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-muted-foreground">
          Priorities and tasks, customers and pipeline, contracts, revenue and renewals, business
          cases and reporting — together, so the numbers and the work tell the same story. Simpler
          than stitching separate tools together, and priced simply, per user.
        </p>
        <div className="mt-10 flex flex-wrap gap-3">
          <Button asChild size="lg">
            <a href="#plans">
              See per-user plans <ArrowRight className="size-4" />
            </a>
          </Button>
          <Button asChild size="lg" variant="ghost">
            <a href="#enquiry">Talk to us about Enterprise</a>
          </Button>
        </div>
      </section>

      <div className="border-y border-border/60 bg-card/30">
        <Section eyebrow="Why businesses use it">
          <SectionHeading>Fewer tools, less admin, better visibility</SectionHeading>
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {[
              {
                title: "Customers and deals in one place",
                body: "Accounts, contacts, opportunities and a simple pipeline sit beside the work itself, with account groups and sub-brands where you need them.",
              },
              {
                title: "Contracts, revenue and renewals",
                body: "Track what you've sold: payment schedules, credit notes and payment holidays with sign-off, renewal dates and customer reviews that come round on time.",
              },
              {
                title: "Reporting without the rebuild",
                body: "Team capacity, delivery timelines, business cases and slide-ready reports generated from the data your people already keep.",
              },
            ].map((item) => (
              <div key={item.title} className="panel p-6">
                <h3 className="text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.body}</p>
              </div>
            ))}
          </div>
        </Section>
      </div>


      <Section eyebrow="Pricing" className="scroll-mt-8">
        <div id="plans" />
        <SectionHeading>Per user, per month</SectionHeading>
        <Lede>
          Every plan below is priced per user. Choose your seat count and the total updates — you pay
          only for the people you add.
        </Lede>

        <div className="panel mt-8 flex flex-wrap items-end gap-4 p-6">
          <div>
            <Label htmlFor="seats">Number of users</Label>
            <Input
              id="seats"
              type="number"
              min={1}
              max={500}
              value={seats}
              onChange={(event) => setSeats(Math.max(1, Number(event.target.value) || 1))}
              className="mt-2 w-32"
            />
          </div>
          <p className="text-sm text-muted-foreground">
            Team: {formatGbp(SEAT_PENCE.team * seats)}/month · Business:{" "}
            {formatGbp(SEAT_PENCE.business * seats)}/month
          </p>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-3">
          {(Object.keys(PLANS) as PaidPlan[]).map((key) => {
            const plan = PLANS[key];
            const effectiveSeats = Math.max(seats, MIN_SEATS[key]);
            return (
              <div key={key} className="panel flex flex-col p-6">
                <h3 className="text-lg font-semibold">{plan.name}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{plan.pitch}</p>
                <p className="mt-4 text-3xl font-bold">
                  {formatGbp(SEAT_PENCE[key])}
                  <span className="text-base font-normal text-muted-foreground">
                    {" "}
                    per user / month
                  </span>
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {formatGbp(SEAT_PENCE[key] * effectiveSeats)}/month for {effectiveSeats}{" "}
                  {effectiveSeats === 1 ? "user" : "users"}
                  {MIN_SEATS[key] > 1 ? ` · minimum ${MIN_SEATS[key]} users` : ""}
                </p>
                <ul className="mt-4 space-y-2 text-sm">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex gap-2">
                      <Check className="mt-0.5 size-4 shrink-0 text-primary" />
                      <span className="text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className="mt-6"
                  onClick={() => setCheckoutPlan(checkoutPlan === key ? null : key)}
                >
                  {checkoutPlan === key ? "Close checkout" : `Get ${plan.name}`}
                </Button>
              </div>
            );
          })}

          <div className="panel flex flex-col p-6">
            <h3 className="text-lg font-semibold">Enterprise</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              For organisations needing bespoke rollout, security review or invoicing.
            </p>
            <p className="mt-4 text-3xl font-bold">
              Custom<span className="text-base font-normal text-muted-foreground"> pricing</span>
            </p>
            <ul className="mt-4 space-y-2 text-sm">
              {[
                "Volume per-user pricing",
                "Onboarding and rollout support",
                "Custom classification and reporting",
                "Invoicing and procurement",
              ].map((feature) => (
                <li key={feature} className="flex gap-2">
                  <Check className="mt-0.5 size-4 shrink-0 text-primary" />
                  <span className="text-muted-foreground">{feature}</span>
                </li>
              ))}
            </ul>
            <Button asChild variant="secondary" className="mt-6">
              <a href="#enquiry">Talk to us</a>
            </Button>
          </div>
        </div>

        {checkoutPlan ? (
          <div className="panel mt-6 p-6">
            <p className="text-sm text-muted-foreground">
              {PLANS[checkoutPlan].name} · {Math.max(seats, MIN_SEATS[checkoutPlan])} users ·{" "}
              {formatGbp(SEAT_PENCE[checkoutPlan] * Math.max(seats, MIN_SEATS[checkoutPlan]))}/month
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              You&apos;ll need to be signed in with a billing email on your account to complete
              checkout. <Link to="/auth" className="text-primary hover:underline">Sign in</Link>.
            </p>
            <div className="mt-4">
              <PlanCheckout
                priceId={PLANS[checkoutPlan].priceId}
                seats={Math.max(seats, MIN_SEATS[checkoutPlan])}
              />
            </div>
          </div>
        ) : null}
      </Section>

      <div className="border-t border-border/60 bg-card/30">
        <Section eyebrow="Enterprise">
          <div id="enquiry" className="scroll-mt-8" />
          <SectionHeading>Tell us about your team</SectionHeading>
          <Lede>
            Send a few details and we&apos;ll come back with pricing and a rollout plan for your
            organisation.
          </Lede>
          <EnquiryForm seats={seats} />
        </Section>
      </div>

      <MarketingFooter />
    </div>
  );
}

function EnquiryForm({ seats }: { seats: number }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sent, setSent] = useState(false);

  const mutation = useMutation({
    mutationFn: async () => {
      const result = await submitBusinessEnquiry({
        data: {
          name: form.name,
          email: form.email,
          company: form.company,
          message: form.message,
          teamSize: `${seats}`,
          planInterest: "enterprise",
        },
      });
      if ("error" in result) throw new Error(result.error);
      return result;
    },
    onSuccess: () => {
      setSent(true);
      setForm({ name: "", email: "", company: "", message: "" });
      toast.success("Thanks — we'll be in touch shortly.");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (sent) {
    return (
      <div className="panel mt-8 p-6">
        <h3 className="text-lg font-semibold">Enquiry received</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          We&apos;ll reply to your work email with pricing and next steps.
        </p>
      </div>
    );
  }

  return (
    <form
      className="panel mt-8 grid gap-4 p-6 md:grid-cols-2"
      onSubmit={(event) => {
        event.preventDefault();
        mutation.mutate();
      }}
    >
      <div>
        <Label htmlFor="enq-name">Your name</Label>
        <Input
          id="enq-name"
          value={form.name}
          onChange={(event) => setForm({ ...form, name: event.target.value })}
          required
          className="mt-2"
        />
      </div>
      <div>
        <Label htmlFor="enq-email">Work email</Label>
        <Input
          id="enq-email"
          type="email"
          value={form.email}
          onChange={(event) => setForm({ ...form, email: event.target.value })}
          required
          className="mt-2"
        />
      </div>
      <div className="md:col-span-2">
        <Label htmlFor="enq-company">Company</Label>
        <Input
          id="enq-company"
          value={form.company}
          onChange={(event) => setForm({ ...form, company: event.target.value })}
          required
          className="mt-2"
        />
      </div>
      <div className="md:col-span-2">
        <Label htmlFor="enq-message">What are you trying to fix?</Label>
        <Textarea
          id="enq-message"
          value={form.message}
          onChange={(event) => setForm({ ...form, message: event.target.value })}
          rows={4}
          className="mt-2"
        />
      </div>
      <div className="md:col-span-2">
        <Button type="submit" size="lg" disabled={mutation.isPending}>
          {mutation.isPending ? "Sending…" : "Send enquiry"}
        </Button>
        <p className="mt-2 text-xs text-muted-foreground">
          We&apos;ll use these details only to reply about MyPriorities for Business.
        </p>
      </div>
    </form>
  );
}
